import { getHashtagSearch } from 'js/social/api/hashtag_search_api';
import { Hashtag } from '../types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '../useDebounce';
import { useMemo } from 'react';

const QUERY_KEY = 'social-hashtag-search';

/**
 * Given a hashtag query, returns the associated hashtag classifications
 * along with whether it's still loading.
 * @param search The hashtag search query.
 */
export function useHashtagSearch(search: string | undefined): {
  isLoading: boolean;
  hashtags: Hashtag[];
} {
  const client = useQueryClient();

  const hasEmptyPrefixData = useMemo(() => {
    if (!search) return false;

    for (let i = 1; i < search.length; i++) {
      const prefix = search.substring(0, search.length - i);
      const state = client.getQueryState([QUERY_KEY, prefix]);
      if (
        state &&
        state.status === 'success' &&
        !state.isInvalidated &&
        JSON.stringify(state.data) === '[]'
      ) {
        return true;
      }
    }
    return false;
  }, [search, client]);

  const queryKey = [QUERY_KEY, search];
  const invalidated = client.getQueryState(queryKey)?.isInvalidated ?? true;
  const { value: debouncedSearch, debouncing } = useDebounce(
    search,
    invalidated ? 250 : 0,
  );
  const enabled = debouncedSearch !== undefined && !hasEmptyPrefixData;
  const { data: hashtags, isLoading: queryLoading } = useQuery({
    enabled,
    staleTime: 60000,
    queryKey: [QUERY_KEY, debouncedSearch],
    queryFn: () => getHashtagSearch(debouncedSearch ?? ''),
  });
  const loading = debouncing || queryLoading;

  // Always include the user's query as the last result
  // so it's obvious they're allowed to use new hashtags.
  const defaultHashtags =
    search !== undefined ? [{ id: 0, hashtag: search }] : [];
  return {
    isLoading: enabled && loading,
    hashtags: (hashtags ?? []).concat(defaultHashtags),
  };
}
